/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { SelectionBar } from 'components/UI/V3/SelectionBar/SelectionBar';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { useDrawerContext } from 'components/UI/V3/Drawer/DrawerContext';
import { useDebounce } from 'hooks/useDebounce';
import { useOrganizationId } from 'hooks/useOrganizationId';
import { AudienceCustomer, AudienceCustomersPage, AudienceSelection } from 'model/emailMarketing';
import { fetchAudienceCustomersThunk } from 'actions/emailMarketing/EmailMarketingActions';
import { CampaignDraft, EmailLocation } from '../../../types';
import { formatNumber } from '../../../utils';
import { FilterPill } from '../../../UI/FilterPill/FilterPill';
import CheckIcon from './CheckIcon';
import styles from './Audience.module.scss';

const PAGE_SIZE = 30;
// Saved picks are loaded once so the Selected / Unselected tabs can list them.
const MAX_SAVED_PICKS = 500;

type FilterTab = 'all' | 'selected' | 'unselected';

interface EditRecipientsDrawerProps {
    // Audience of the campaign; the list always shows it without manual picks.
    selection: AudienceSelection;
    poolSize: number;
    locations: Array<EmailLocation>;
    locationIds: Array<number>;
    selectedIds: CampaignDraft['selectedCustomerIds'];
    excludedIds: Array<number>;
    // eslint-disable-next-line no-unused-vars
    onSave: (selectedIds: CampaignDraft['selectedCustomerIds'], excludedIds: Array<number>) => void;
    onClose: () => void;
}

const matches = (customer: AudienceCustomer, term: string, locationFilter: Set<number>) =>
    (locationFilter.size === 0 || locationFilter.has(customer.locationId)) &&
    (!term || `${customer.firstName} ${customer.lastName} ${customer.email}`.toLowerCase().includes(term));

// Org version of the marketplace "Edit clients" drawer: the recipients are paged, searched and filtered
// by the backend (CUD-5778); picks stay local until Save, like the marketplace picker.
const RecipientsContent: React.FC<Omit<EditRecipientsDrawerProps, 'onClose'>> = ({
    selection,
    poolSize,
    locations,
    locationIds,
    selectedIds: initialSelectedIds,
    excludedIds: initialExcludedIds,
    onSave
}) => {
    const dispatch = useDispatch();
    const { close } = useDrawerContext();
    const organizationId = useOrganizationId();

    // 'all' = exclude mode (Select all on), Set = include mode.
    const [selected, setSelected] = useState<'all' | Set<number>>(
        initialSelectedIds === 'all' ? 'all' : new Set(initialSelectedIds)
    );
    const [excluded, setExcluded] = useState<Set<number>>(new Set(initialExcludedIds));
    // Rows of the picked / excluded customers, for the tab that lists them.
    const [known, setKnown] = useState<Map<number, AudienceCustomer>>(new Map());
    const [search, setSearch] = useState('');
    const term = useDebounce(search.trim().toLowerCase(), 300);
    // Sending locations to list; empty = all locations.
    const [locationFilter, setLocationFilter] = useState<Set<number>>(new Set());
    const [tab, setTab] = useState<FilterTab>('all');

    const [rows, setRows] = useState<Array<AudienceCustomer>>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const isAllMode = selected === 'all';
    const isChecked = (id: number) => (isAllMode ? !excluded.has(id) : (selected as Set<number>).has(id));
    const pool = useMemo<AudienceSelection>(
        () => ({ ...selection, excludeIds: [], customerIds: null }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(selection)]
    );

    const remember = (customers: Array<AudienceCustomer>) =>
        setKnown(prev => {
            const next = new Map(prev);
            customers.forEach(customer => next.set(customer.id, customer));
            return next;
        });

    // Load the saved picks once, so a reopened draft lists them in its tab.
    useEffect(() => {
        const ids = (initialSelectedIds === 'all' ? initialExcludedIds : initialSelectedIds).slice(0, MAX_SAVED_PICKS);
        if (!organizationId || ids.length === 0) return;
        (
            dispatch(
                fetchAudienceCustomersThunk(
                    organizationId,
                    { ...pool, customerIds: ids },
                    { page: 1, size: ids.length }
                ) as any
            ) as Promise<AudienceCustomersPage>
        )
            .then(result => {
                if (requestRef.current !== -1) remember(result.customers);
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    // Only the latest request may update the list (fast typing, closing the drawer mid-request).
    const requestRef = useRef(0);
    useEffect(
        () => () => {
            requestRef.current = -1;
        },
        []
    );

    const loadPage = (nextPage: number) => {
        if (!organizationId) return;
        const requestId = ++requestRef.current;
        setLoading(true);
        (
            dispatch(
                fetchAudienceCustomersThunk(organizationId, pool, {
                    page: nextPage,
                    size: PAGE_SIZE,
                    search: term || undefined,
                    locationIds: Array.from(locationFilter)
                }) as any
            ) as Promise<AudienceCustomersPage>
        )
            .then(result => {
                if (requestId !== requestRef.current) return;
                setRows(prev => (nextPage === 1 ? result.customers : [...prev, ...result.customers]));
                setPage(nextPage);
                setTotalPages(result.totalPages);
                setTotalRecords(result.totalRecords);
            })
            .catch(() => undefined)
            .finally(() => {
                if (requestId === requestRef.current) setLoading(false);
            });
    };

    // New search or location: start over from the first page.
    useEffect(() => {
        loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, pool, term, locationFilter]);

    // Infinite scroll over the server pages.
    const hasMore = page < totalPages;
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || loading) return;
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) loadPage(page + 1);
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loading, page]);

    // The picks the user changed live here; the rest of the audience comes page by page from the server.
    const localIds = isAllMode ? excluded : (selected as Set<number>);
    const localRows = Array.from(localIds)
        .map(id => known.get(id))
        .filter((customer): customer is AudienceCustomer => !!customer && matches(customer, term, locationFilter));
    const localTab: FilterTab = isAllMode ? 'unselected' : 'selected';

    const unchecked = isAllMode ? localRows.length : Math.max(totalRecords - localRows.length, 0);
    const checked = isAllMode ? Math.max(totalRecords - localRows.length, 0) : localRows.length;
    const visibleRows =
        tab === 'all'
            ? rows
            : tab === localTab
            ? localRows
            : rows.filter(customer => (tab === 'selected' ? isChecked(customer.id) : !isChecked(customer.id)));
    const showSentinel = tab !== localTab && hasMore;

    const totalSelected = isAllMode ? Math.max(poolSize - excluded.size, 0) : (selected as Set<number>).size;

    const toggleSelectAll = () => {
        setSelected(isAllMode ? new Set() : 'all');
        setExcluded(new Set());
    };

    const toggle = (customer: AudienceCustomer) => {
        remember([customer]);
        const update = (prev: Set<number>) => {
            const next = new Set(prev);
            if (next.has(customer.id)) next.delete(customer.id);
            else next.add(customer.id);
            return next;
        };
        if (isAllMode) setExcluded(update);
        else setSelected(prev => update(prev as Set<number>));
    };

    const filterLocations = locations.filter(location => locationIds.includes(location.id));
    const filterLabel =
        locationFilter.size === 0
            ? 'All locations'
            : locationFilter.size === 1
            ? filterLocations.find(location => locationFilter.has(location.id))?.name ?? 'Location'
            : `${locationFilter.size} locations`;

    // Multi-select: "All locations" clears the filter; picking every location also means all.
    const toggleLocation = (id: number | null) => {
        if (id === null) return setLocationFilter(new Set());
        setLocationFilter(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next.size === filterLocations.length ? new Set() : next;
        });
    };

    return (
        <>
            <DrawerHeader
                sticky
                title="Edit recipients"
                actions={
                    <FilterPill
                        label={filterLabel}
                        icon={faLocationDot}
                        active={locationFilter.size > 0}
                        className={styles.locationPill}
                        align="right"
                    >
                        {() => (
                            <div className={styles.locationMenu}>
                                {[{ id: null, name: 'All locations' }, ...filterLocations].map(option => (
                                    <button
                                        key={option.id ?? 'all'}
                                        type="button"
                                        className={styles.locationOption}
                                        onClick={() => toggleLocation(option.id)}
                                    >
                                        <CheckIcon
                                            checked={
                                                option.id === null
                                                    ? locationFilter.size === 0
                                                    : locationFilter.has(option.id)
                                            }
                                        />
                                        {option.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </FilterPill>
                }
            />
            <DrawerBody className={styles.drawerBody}>
                <p className={styles.drawerHint}>
                    Uncheck customers you want to exclude, or turn off Select all and pick the ones to include. Each
                    customer is listed once, with the location the email is sent from.
                </p>

                <SearchBar
                    searchText={search}
                    placeholder="Search by name or email"
                    className={styles.search}
                    onChangeSearchText={setSearch}
                />

                <SelectionBar
                    options={[
                        { id: 'all', name: `All (${formatNumber(totalRecords)})` },
                        { id: 'selected', name: `Selected (${formatNumber(checked)})` },
                        { id: 'unselected', name: `Unselected (${formatNumber(unchecked)})` }
                    ]}
                    selectedId={tab}
                    background="#f8f8f8"
                    className={styles.tabs}
                    onSelect={id => setTab(id as FilterTab)}
                />

                <div className={styles.list}>
                    <button
                        type="button"
                        className={styles.allRow}
                        onClick={toggleSelectAll}
                    >
                        <CheckIcon checked={isAllMode} />
                        <span className={styles.rowTitle}>Select all</span>
                    </button>

                    {visibleRows.map(customer => (
                        <button
                            key={customer.id}
                            type="button"
                            className={styles.row}
                            onClick={() => toggle(customer)}
                        >
                            <CheckIcon checked={isChecked(customer.id)} />
                            <span className={styles.rowText}>
                                <span className={styles.rowTitle}>
                                    {customer.firstName} {customer.lastName}
                                </span>
                                <span className={styles.rowMeta}>{customer.email}</span>
                            </span>
                            <span className={styles.locationChip}>{customer.locationName}</span>
                        </button>
                    ))}

                    {loading && rows.length === 0 && (
                        <div className={styles.loadingRow}>
                            <Spinner size={32} />
                        </div>
                    )}
                    {!loading && visibleRows.length === 0 && !showSentinel && (
                        <p className={styles.empty}>No customers match these filters.</p>
                    )}
                    {showSentinel && (
                        <div
                            ref={sentinelRef}
                            className={styles.sentinel}
                        />
                    )}
                </div>
            </DrawerBody>
            <DrawerFooter className={styles.footer}>
                <span className={styles.footerCount}>
                    <strong>{formatNumber(totalSelected)}</strong> of {formatNumber(poolSize)} will receive it
                </span>
                <Button
                    variant="outlined"
                    label="Cancel"
                    onClick={close}
                />
                <Button
                    variant="black"
                    label="Save"
                    disabled={totalSelected === 0}
                    onClick={() => {
                        onSave(
                            isAllMode ? 'all' : Array.from(selected as Set<number>),
                            isAllMode ? Array.from(excluded) : []
                        );
                        close();
                    }}
                />
            </DrawerFooter>
        </>
    );
};

export const EditRecipientsDrawer: React.FC<EditRecipientsDrawerProps> = ({ onClose, ...props }) => (
    <Drawer onClose={onClose}>
        <RecipientsContent {...props} />
    </Drawer>
);

export default EditRecipientsDrawer;
